import {
  InformalFeedbackRequest,
  InformalFeedbackReviewScore,
  SubmittedInformalFeedbackReview,
} from 'api/clients/InformalFeedbackRequestsClient'
import { InformalFeedbackReviewState, InformalFeedbackViewerState } from 'api/clients/InformalFeedbackRequestsClient'
import { RequestAnotherReviewButton } from '../Buttons/RequestAnotherReviewButton'
import { useAuth } from 'hooks/UseAuth'
import { EndReviewButton } from '../Buttons/EndReviewButton'
import { SubmitReviewButton } from '../Buttons/SubmitReviewButton'
import { MarkViewerAsReadButton } from '../Buttons/MarkViewerAsReadButton'
import Styles from './ReviewKeyAction.module.scss'
import { getSubmittedReviews } from 'utils/informalFeedbackRequest'

export interface ReviewKeyActionProps {
  request: InformalFeedbackRequest
}

export const getLastReview = (request: InformalFeedbackRequest) => {
  return getSubmittedReviews(request).sort((a, b) => {
    if (a.submitted_at === null || b.submitted_at === null) {
      return 0
    }
    return Date.parse(a.submitted_at) - Date.parse(b.submitted_at)
  })[0] as SubmittedInformalFeedbackReview
}

export const ReviewKeyAction = ({ request }: ReviewKeyActionProps) => {
  const auth = useAuth()
  const reviewHasEnded = !!request.closed_on
  if (reviewHasEnded) {
    return <span className={Styles.actionLabel}>Ended</span>
  }
  const currentUserId = auth?.id
  const currentUserIsRequester = currentUserId === request.requester.id
  if (currentUserIsRequester) {
    const latestReview = getLastReview(request)
    if (!latestReview) {
      return <EndReviewButton request={request} />
    }
    switch (latestReview.score) {
      case InformalFeedbackReviewScore.NeedsChanges:
      case InformalFeedbackReviewScore.NeedsMajorChanges:
        return <RequestAnotherReviewButton review={latestReview} request={request} />
      case InformalFeedbackReviewScore.Approved:
      default:
        return <EndReviewButton request={request} />
    }
  }
  const currentUserReadReceipt = request.viewers.find((viewer) => viewer.viewer.id === currentUserId)
  if (currentUserReadReceipt) {
    switch (currentUserReadReceipt?.state) {
      case InformalFeedbackViewerState.Completed:
        return <span className={Styles.actionLabel}>You Marked as Read</span>
      default:
        return <MarkViewerAsReadButton request={request} />
    }
  }

  const currentUserReview = request.reviews.find((review) => review.reviewer.id === currentUserId)
  switch (currentUserReview?.state) {
    case undefined:
      return null // User has no review
    case InformalFeedbackReviewState.Submitted:
      return <span className={Styles.actionLabel}>You Reviewed</span>
    case InformalFeedbackReviewState.Dismissed:
      return <span className={Styles.actionLabel}>You Skipped</span>
    default:
      return <SubmitReviewButton request={request} />
  }
}
